import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { Decimal } from '@mlm/shared';
import { apiErrorMessage, fmtPeso, performGatewayHandoff, store } from '../lib/api';
import { visibleCartMethods } from '../lib/cart-methods';
import { isNativeShell } from '@mlm/ui/lib/native-shell';
import { qk } from '../lib/queries';
import { useCartStore } from '../lib/cart-store';
import { Block, CoachButton, Eyebrow, Loading, Screen } from '../components/primitives';

/**
 * Cart and checkout on one screen: on a phone, splitting them is two taps and a
 * lost basket for nothing. Money shown here is a Decimal sum of the cached line
 * prices and is DISPLAY ONLY — `POST /orders` re-prices every line server-side
 * from the product ids, so what is charged never comes from this device.
 */
export function CartPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const lines = useCartStore((s) => s.lines);
  const setQty = useCartStore((s) => s.setQty);
  const remove = useCartStore((s) => s.remove);
  const clear = useCartStore((s) => s.clear);
  const [methodCode, setMethodCode] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const ctx = useQuery({
    queryKey: qk.shopContext,
    queryFn: store.paymentMethods,
    staleTime: 5 * 60_000,
  });

  // What this cart may offer, and how many gateway methods the phone app hid
  // (see lib/cart-methods.ts for both rules).
  const { methods, hiddenGateways } = visibleCartMethods(ctx.data?.methods ?? [], {
    native: isNativeShell(),
  });
  const method = methods.find((m) => m.code === methodCode) ?? null;

  const subtotal = lines.reduce(
    (sum, l) => sum.plus(new Decimal(l.unitPrice).times(l.qty)),
    new Decimal(0),
  );

  const placeOrder = useMutation({
    mutationFn: async () => {
      const order = await store.checkout({
        items: lines.map((l) => ({
          productId: l.productId,
          qty: l.qty,
          variantId: l.variantId,
        })),
        paymentMethodCode: method!.code,
      });
      // The order exists now, so the basket has done its job either way.
      clear();
      await qc.invalidateQueries({ queryKey: qk.myOrders });
      if (method!.settlement === 'gateway') {
        performGatewayHandoff(await store.initiateGateway(order.id));
        return null;
      }
      return order.id;
    },
    onSuccess: (orderId) => {
      if (orderId) navigate(`/orders/${orderId}`, { replace: true });
    },
    onError: setError,
  });

  if (lines.length === 0) {
    return (
      <Screen eyebrow="Shop" title="Your cart" back="/shop">
        <div className="px-5 py-14 text-center">
          <Eyebrow>Empty</Eyebrow>
          <p className="mt-2 text-[0.93rem] text-ink/70">Nothing in the cart yet.</p>
          <Link to="/shop" className="mt-6 inline-block">
            <CoachButton>Browse the store</CoachButton>
          </Link>
        </div>
      </Screen>
    );
  }

  return (
    <Screen eyebrow="Shop" title="Your cart" back="/shop">
      <ul className="border-b border-border">
        {lines.map((l) => (
          <li
            key={`${l.productId}:${l.variantId ?? ''}`}
            className="flex items-start gap-4 border-b border-border px-5 py-4 last:border-b-0"
          >
            <div className="min-w-0 flex-1">
              <p className="text-[0.93rem] leading-snug text-ink">{l.name}</p>
              {l.variantName && <Eyebrow className="mt-1">{l.variantName}</Eyebrow>}
              <p className="mt-1 text-[0.93rem] text-ink/70">{fmtPeso(l.unitPrice)} each</p>
              <div className="mt-3 flex items-center gap-3">
                <CoachButton
                  onClick={() => setQty(l.productId, l.variantId, l.qty - 1)}
                  aria-label={`Less ${l.name}`}
                >
                  &minus;
                </CoachButton>
                <span className="min-w-8 text-center text-[0.95rem] text-ink">{l.qty}</span>
                <CoachButton
                  onClick={() => setQty(l.productId, l.variantId, l.qty + 1)}
                  aria-label={`More ${l.name}`}
                >
                  +
                </CoachButton>
                <button
                  type="button"
                  onClick={() => remove(l.productId, l.variantId)}
                  aria-label={`Remove ${l.name}`}
                  className="ml-2 text-sage-deep hover:text-ink"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </button>
              </div>
            </div>
            <p className="shrink-0 font-display text-[1.05rem] font-semibold text-ink">
              {fmtPeso(new Decimal(l.unitPrice).times(l.qty).toString())}
            </p>
          </li>
        ))}
      </ul>

      <Block eyebrow="Payment" title="How are you paying?">
        {ctx.isPending ? (
          <Loading label="Loading payment methods" />
        ) : methods.length === 0 ? (
          <p>
            {hiddenGateways > 0
              ? 'Card and online payments are made on the website, not inside the app. Open the store on the website to pay for this.'
              : 'No payment method is available on your account right now.'}
          </p>
        ) : (
          <ul className="mt-3 space-y-2">
            {methods.map((m) => (
              <li key={m.code}>
                <button
                  type="button"
                  onClick={() => setMethodCode(m.code)}
                  className={[
                    'flex min-h-11 w-full items-center justify-between gap-3 border px-4 py-3 text-left transition-colors',
                    m.code === methodCode
                      ? 'border-verde bg-surface'
                      : 'border-[rgba(16,32,26,0.22)] hover:border-[rgba(16,32,26,0.5)]',
                  ].join(' ')}
                >
                  <span className="text-[0.93rem] text-ink">{m.label}</span>
                  {m.balance !== null && (
                    <span className="font-mono text-[0.68rem] uppercase tracking-[0.14em] text-sage-deep">
                      {fmtPeso(m.balance)}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
        {method?.instructions && (
          <p className="mt-4 border border-border px-4 py-3 text-[0.9rem] leading-relaxed text-ink/80">
            {method.instructions}
          </p>
        )}
      </Block>

      <div className="px-5 py-6">
        <div className="flex items-baseline justify-between">
          <Eyebrow>Subtotal</Eyebrow>
          <p className="font-display text-[1.5rem] font-semibold text-ink">
            {fmtPeso(subtotal.toString())}
          </p>
        </div>
        <p className="mt-2 text-[0.85rem] leading-relaxed text-ink/60">
          Shipping and any fees are added when the order is placed.
        </p>

        {error != null && (
          <p className="mt-4 border border-[rgba(176,141,63,0.4)] px-4 py-3 text-[0.9rem] text-ink/85">
            {apiErrorMessage(error)}
          </p>
        )}

        <CoachButton
          kind="gold"
          block
          className="mt-6"
          disabled={!method || placeOrder.isPending}
          onClick={() => {
            setError(null);
            placeOrder.mutate();
          }}
        >
          {placeOrder.isPending ? 'Placing your order' : 'Place order'}
        </CoachButton>
      </div>
    </Screen>
  );
}
